import { ref } from 'vue'
import { useStore } from 'vuex'

import { getSignedDownloadUrl } from '@/api/files'

/**
 * Resolve a signed download URL and open it in a new tab. De-dupes concurrent
 * clicks per id and surfaces failures as a notification.
 */
export function useSignedDownload() {
  const store = useStore()
  const signingIds = ref(new Set())

  const isSigning = (id) => signingIds.value.has(id)

  async function download(appName, modelName, id) {
    if (signingIds.value.has(id)) return
    signingIds.value.add(id)
    // Open the tab now, while the click's user activation is still valid —
    // window.open() after the await is treated as untrusted and popup-blocked
    // (Safari/strict). 'noopener' nulls the handle, so sever the opener by hand.
    const tab = window.open('', '_blank')
    if (tab) tab.opener = null
    try {
      const { url } = await getSignedDownloadUrl(appName, modelName, id)
      if (tab) tab.location = url
      else window.open(url, '_blank', 'noopener')
    } catch (err) {
      if (tab) tab.close()
      store.commit('notify', {
        type: 'error',
        message:
          err.response?.data?.message ||
          err.message ||
          'Could not get a download link.',
      })
    } finally {
      signingIds.value.delete(id)
    }
  }

  return { isSigning, download }
}
